import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Crown, FileDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface MaintenanceHeaderProps {
  isProAccount: boolean;
  onAddClick: () => void;
  disableAdd?: boolean;
  onExportPDF?: (annee: number) => void;
}

const MaintenanceHeader = ({ isProAccount, onAddClick, disableAdd = false, onExportPDF }: MaintenanceHeaderProps) => {
  const currentYear = new Date().getFullYear();
  const [exportOpen, setExportOpen] = useState(false);
  const [exportYear, setExportYear] = useState<number>(currentYear);
  const availableYears = Array.from({ length: 7 }, (_, i) => currentYear + 1 - i);

  const handleConfirmExport = () => {
    onExportPDF?.(exportYear);
    setExportOpen(false);
  };

  return (
    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-nautic flex items-center gap-2 flex-wrap">
          Maintenance
          {!isProAccount && (
            <>
              <Crown className="h-5 w-5 lg:h-6 lg:w-6 text-nautic-gold" />
              <span className="text-sm lg:text-lg text-gray-600">(3 maintenances en compte gratuit)</span>
            </>
          )}
        </h1>
        <p className="text-muted-foreground mt-1">
          Gérez la maintenance de vos navires
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
        {onExportPDF && (
          <Button
            variant="outline"
            className="border-nautic text-nautic hover:bg-nautic hover:text-white w-full sm:w-auto"
            onClick={() => setExportOpen(true)}
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        )}
        <Button 
          className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold w-full sm:w-auto disabled:opacity-50"
          onClick={onAddClick}
          disabled={disableAdd}
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une maintenance
        </Button>
      </div>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Exporter les maintenances en PDF</DialogTitle>
            <DialogDescription>
              Sélectionnez l'année pour générer le PDF des maintenances effectuées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Année</Label>
            <Select value={exportYear.toString()} onValueChange={(v) => setExportYear(parseInt(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Annuler</Button>
            <Button className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold" onClick={handleConfirmExport}>
              <FileDown className="h-4 w-4 mr-2" />
              Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceHeader;
